import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Truck } from "lucide-react";
import {
  Grid, FormControl, Typography, Box, RadioGroup, FormControlLabel,
  Radio, TextField, InputAdornment, Tooltip, useMediaQuery, useTheme,
  CircularProgress, Button, Paper,
} from '@mui/material';
import local from './assets/entregaLocal.png';
import sucursalImg from './assets/entregaSucursal.png';
import transporte from './assets/envioTransporte.png';
import andreaniImg from './assets/envioAndreani.png';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { getSucursalesInfo } from "../../services/SucursalesServices";
import { getClientesById, patchClientes } from "../../services/ClientesServices";
import { AuthContext } from "../../context/authContext";
import ModalDireccionEntrega from "../../components/Modals/ModalDireccionEntrega";
import ModalSucursal from "../../components/Modals/ModalSucursal";

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)',
    backgroundColor: 'var(--bg-subtle)', transition: 'all 0.2s ease',
    '& fieldset': { border: '2px solid transparent' },
    '&:hover fieldset': { border: '2px solid var(--border-hover)' },
    '&.Mui-focused fieldset': { border: '2px solid var(--accent)' },
    '&.Mui-focused': { backgroundColor: 'var(--bg-card)' },
  },
  '& .MuiInputLabel-root': { fontFamily: 'var(--font-body)', fontSize: '0.88rem', '&.Mui-focused': { color: 'var(--accent)' } },
};

/* ── Shipping Option Card ── */
const ShippingOption = ({ value, checked, onChange, image, label }) => (
  <Paper
    elevation={0}
    onClick={() => onChange({ target: { value } })}
    sx={{
      border: checked ? '2px solid var(--accent)' : '1.5px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      p: 2,
      cursor: 'pointer',
      backgroundColor: checked ? 'var(--accent-soft)' : 'var(--bg-card)',
      transition: 'all 0.2s ease',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 1.5,
      position: 'relative',
      overflow: 'hidden',
      // Ancho fijo para que todas las cards sean iguales y el flex-wrap centre bien
      width: { xs: 130, sm: 150 },
      minWidth: { xs: 130, sm: 150 },
      '&:hover': { borderColor: 'var(--accent)', boxShadow: 'var(--shadow-sm)' },
    }}
  >
    {checked && (
      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 20, color: 'var(--accent)' }} />
      </Box>
    )}
    <img src={image} alt={label} style={{ width: 70, height: 70, objectFit: 'contain' }} />
    <Typography sx={{
      fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.82rem',
      color: checked ? 'var(--accent)' : 'var(--text-primary)', textAlign: 'center',
    }}>
      {label}
    </Typography>
    <Radio value={value} checked={checked} sx={{ display: 'none' }} />
  </Paper>
);

const SeccionEnvios = ({
  onBack, onNext, retiroLocal, entregaSucursal, envioTransporte, envioAndreani,
  obsEntregaSucursal, setObsEntregaSucursal, obsEnvioTransporte, setObsEnvioTransporte,
  loading, shipping, setShipping, shippingMethod, handleShippingMethodChange,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [codigoPostal, setCodigoPostal] = useState("");
  const [mostrarOpciones, setMostrarOpciones] = useState(false);
  const [totalEnvio, setTotalEnvio] = useState(false);
  const [errorCodigoPostal, setErrorCodigoPostal] = useState(false);
  const [errorMetodoEnvio, setErrorMetodoEnvio] = useState(false);
  const [verificandoCP, setVerificandoCP] = useState(false);
  const [selectedSucursal, setSelectedSucursal] = useState(null);
  const [sucursales, setSucursales] = useState(null);
  const [openDireccionModal, setOpenDireccionModal] = useState(false);
  const [openSucursalModal, setOpenSucursalModal] = useState(false);
  const [clienteDoc, setClienteDoc] = useState(0);
  const [clienteDireccion, setClienteDireccion] = useState({});
  const [direccionCliente, setDireccionCliente] = useState(null);
  const [direccionGuardada, setDireccionGuardada] = useState(null);
  const { user } = useContext(AuthContext);

  useEffect(() => { getSucursalesData(); getClienteData(); }, [openDireccionModal, openSucursalModal]);
  useEffect(() => {
    if (shippingMethod === "transporte") setOpenDireccionModal(true);
    if (shippingMethod === "sucursal") setOpenSucursalModal(true);
  }, [shippingMethod]);

  const getSucursalesData = async () => {
    try { setSucursales(await getSucursalesInfo()); } catch (e) { console.error(e); }
  };

  const getClienteData = async () => {
    try {
      const idUser = user.empleadoUser?.length > 0 ? user.empleadoUser[0].id : user.id;
      const data = await getClientesById(idUser);
      setClienteDoc(data.cli_cuit);
      if (!obsEnvioTransporte) {
        setClienteDireccion(data?.cli_direccion_entrega ? {
          calle: data.cli_direccion_entrega, nro: data.cli_nro_calle_entrega, piso: data.cli_piso_entrega,
          dpto: data.cli_dpto_entrega, cli_pcia_entrega: data.cli_pcia_entrega, cli_depto_entrega: data.cli_depto_entrega,
          cli_loc_entrega: data.cli_loc_entrega, cp: data.cli_cp_entrega, observacion: data.cli_direccion_entrega_observ,
        } : null);
      }
    } catch (e) { console.error(e); }
  };

  const opciones = [
    { nombre: "OCA",     tipo: "DOMICILIO", precio: 2000 },
    { nombre: "OCA",     tipo: "SUCURSAL",  precio: 3000 },
    { nombre: "Andreani",tipo: "DOMICILIO", precio: 4000 },
    { nombre: "Pickit",  tipo: "SUCURSAL",  precio: 5000 },
  ];

  const handleSelectShipping = (op) => {
    const envio = { codigoPostal, metodo: `${op.nombre} - ${op.tipo}`, precioEnvio: op.precio, precioTotal: Number(op.precio) };
    setTotalEnvio(envio.precioTotal); setShipping(envio); setErrorMetodoEnvio(false);
  };

  const onShippingMethodChange = (e) => {
    handleShippingMethodChange(e);
    setObsEnvioTransporte(null); setObsEntregaSucursal(null); setCodigoPostal("");
    setMostrarOpciones(false); setTotalEnvio(false); setShipping(null);
    setErrorCodigoPostal(false); setErrorMetodoEnvio(false);
  };

  const handleDireccion = async (accion, dir) => {
    const envio = { tipo: "Envio por transporte", calle: dir.calle, nro: dir.nro, piso: dir.piso, dpto: dir.dpto, cp: dir.cp, localidad: dir.localidad, departamento: dir.departamento, provincia: dir.provincia, observacion: dir.observacion };
    setObsEnvioTransporte(envio); setShipping(envio);
  };

  const handleSeleccionSucursal = (suc) => {
    const envio = { tipo: "Retira desde una Sucursal", nombre: suc.nombre, direccion: suc.direccion };
    setShipping(envio); setOpenSucursalModal(false); setErrorMetodoEnvio(false);
    setSelectedSucursal(envio); setObsEntregaSucursal(envio);
  };

  const verificarCodigoPostal = async () => {
    if (codigoPostal.trim().length < 4) { setErrorCodigoPostal(true); setMostrarOpciones(false); return; }
    try {
      setVerificandoCP(true);
      const res = await axios.get(`${process.env.REACT_APP_URL_BACKEND}/api/codigos-postales/${codigoPostal}`);
      if (res.data.exists) { setErrorCodigoPostal(false); setMostrarOpciones(true); }
      else { setErrorCodigoPostal(true); setMostrarOpciones(false); }
    } catch (e) { setErrorCodigoPostal(true); setMostrarOpciones(false); }
    finally { setVerificandoCP(false); }
  };

  const handleNext = () => {
    if (!shippingMethod) { setErrorMetodoEnvio(true); return; }
    if (shippingMethod === "sucursal" && !shipping && !selectedSucursal) { setErrorMetodoEnvio(true); return; }
    if (shippingMethod === "transporte" && !shipping) { setErrorMetodoEnvio(true); return; }
    if (shippingMethod === "andreani" && (!shipping || !codigoPostal || errorCodigoPostal)) { setErrorMetodoEnvio(true); return; }
    onNext();
  };

  return (
    <Box sx={{ mb: 2 }}>

      {/* ── Header ── */}
      <Paper elevation={0} sx={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', backgroundColor: 'var(--bg-card)', mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LocalShippingOutlinedIcon sx={{ color: 'var(--accent)', fontSize: 20 }} />
          </Box>
          <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.05rem', color: 'var(--text-primary)' }}>
            Tipo de Entrega
          </Typography>
        </Box>
      </Paper>

      {/* ── Options ── */}
      <Paper elevation={0} sx={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', backgroundColor: 'var(--bg-card)', p: { xs: 2, sm: 3 } }}>
        <FormControl component="fieldset" fullWidth>
          {/*
            flex-wrap + justify-content: center centra automáticamente
            cualquier cantidad de cards:
              1 card  → centrada sola
              2 cards → centradas en fila
              3 cards → 2 arriba centradas + 1 abajo centrada
              4 cards → 2x2 centrado
          */}
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            justifyContent: 'center',
            alignItems: 'flex-start',
          }}>
            {retiroLocal && (
              <ShippingOption value="local" checked={shippingMethod === "local"} onChange={onShippingMethodChange} image={local} label="Retiro en el local" />
            )}
            {sucursales?.length > 0 && entregaSucursal && (
              <ShippingOption value="sucursal" checked={shippingMethod === "sucursal"} onChange={onShippingMethodChange} image={sucursalImg} label="Entrega en Sucursal" />
            )}
            {envioTransporte && (
              <ShippingOption value="transporte" checked={shippingMethod === "transporte"} onChange={onShippingMethodChange} image={transporte} label="Envío por Encomienda" />
            )}
            {envioAndreani && (
              <ShippingOption value="andreani" checked={shippingMethod === "andreani"} onChange={onShippingMethodChange} image={andreaniImg} label="Envío por Andreani" />
            )}
          </Box>
        </FormControl>

        {/* Modales */}
        <ModalDireccionEntrega
          open={openDireccionModal} onClose={() => setOpenDireccionModal(false)}
          direccionGuardada={direccionGuardada} direccionCliente={clienteDireccion}
          onGuardarDireccion={(dir) => handleDireccion("guardar", dir)}
          onReemplazarDireccion={(dir) => handleDireccion("reemplazar", dir)}
          onUsarDireccionPedido={(dir) => handleDireccion("usar", dir)}
        />
        <ModalSucursal
          open={openSucursalModal} onClose={() => setOpenSucursalModal(false)}
          sucursales={sucursales} onSelect={handleSeleccionSucursal}
        />

        {errorMetodoEnvio && (
          <Typography sx={{ color: 'var(--danger)', textAlign: 'center', mt: 1.5, fontFamily: 'var(--font-body)', fontSize: '0.82rem', fontWeight: 600 }}>
            Debe seleccionar una forma de envío
          </Typography>
        )}

        {/* ── Andreani: CP + opciones ── */}
        {shippingMethod === "andreani" && (
          <Box sx={{ mt: 3 }}>
            <TextField
              label="Código Postal" value={codigoPostal} fullWidth
              onChange={(e) => { const v = e.target.value; if (/^\d*$/.test(v)) { setCodigoPostal(v); setErrorCodigoPostal(false); setMostrarOpciones(false); } }}
              error={errorCodigoPostal}
              helperText={errorCodigoPostal ? "Código postal inválido o no encontrado" : "Ingresá tu código postal"}
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {verificandoCP
                      ? <CircularProgress size={20} sx={{ color: 'var(--accent)' }} />
                      : (
                        <Tooltip title="Verificar código postal">
                          <Truck size={20} onClick={verificarCodigoPostal} style={{ cursor: "pointer", color: "var(--accent)" }} />
                        </Tooltip>
                      )
                    }
                  </InputAdornment>
                ),
              }}
            />

            {mostrarOpciones && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2, justifyContent: 'center' }}>
                {opciones.map((op, idx) => {
                  const selected = shipping?.metodo === `${op.nombre} - ${op.tipo}`;
                  return (
                    <Paper key={idx} elevation={0} onClick={() => handleSelectShipping(op)}
                      sx={{
                        border: selected ? '2px solid var(--accent)' : '1.5px solid var(--border)',
                        borderRadius: 'var(--radius-md)', px: 2, py: 1.5, textAlign: 'center',
                        cursor: 'pointer', minWidth: 100,
                        backgroundColor: selected ? 'var(--accent-soft)' : 'var(--bg-card)',
                        transition: 'all 0.2s ease',
                        '&:hover': { borderColor: 'var(--accent)' },
                      }}>
                      <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.82rem', color: selected ? 'var(--accent)' : 'var(--text-primary)' }}>{op.nombre}</Typography>
                      <Typography sx={{ fontFamily: 'var(--font-body)', fontSize: '0.7rem', color: 'var(--text-muted)' }}>{op.tipo}</Typography>
                      <Typography sx={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.85rem', color: selected ? 'var(--accent)' : 'var(--text-primary)', mt: 0.5 }}>${op.precio}</Typography>
                    </Paper>
                  );
                })}
              </Box>
            )}

            {totalEnvio && (
              <Box sx={{ mt: 2, textAlign: 'right' }}>
                <Typography sx={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>
                  Total Envío: <Typography component="span" sx={{ color: 'var(--accent)', fontWeight: 800 }}>${totalEnvio}</Typography>
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* ── Navegación ── */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5, mt: 3 }}>
        <Button onClick={onBack} variant="outlined" fullWidth startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
          sx={{
            borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-body)', fontWeight: 700,
            fontSize: '0.9rem', textTransform: 'none', py: 1.25,
            color: 'var(--text-secondary)', borderColor: 'var(--border)', borderWidth: '1.5px',
            '&:hover': { backgroundColor: 'var(--bg-subtle)', borderColor: 'var(--border-hover)' },
          }}>
          Volver
        </Button>
        <Button onClick={handleNext} disabled={loading} variant="contained" fullWidth endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
          sx={{
            borderRadius: 'var(--radius-md)', fontFamily: 'var(--font-heading)', fontWeight: 700,
            fontSize: '0.9rem', textTransform: 'none', py: 1.25,
            backgroundColor: 'var(--accent)', boxShadow: '0 4px 12px rgba(233,69,96,0.3)',
            '&:hover': { backgroundColor: 'var(--accent-hover)', transform: 'translateY(-1px)' },
            '&:disabled': { backgroundColor: 'var(--border)', boxShadow: 'none' },
          }}>
          {loading ? "Cargando..." : "Continuar"}
        </Button>
      </Box>

    </Box>
  );
};

export default SeccionEnvios;